import React, { useEffect, useState } from "react";
import {
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tag,
} from "@jem-open/jem-ui";
import { ColumnDef } from "@tanstack/react-table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SyncRun } from "../services/model/SyncRun";
import { SyncRunRecord } from "../services/model/SyncRunRecord";
import { useGetSyncRunRecordsApiService } from "../services/api/GetSyncRunRecordsApiService";
import { BLOCK_STATUS_TAG, CHANNEL_LABELS, SYNC_STATUS_TAG } from "../constants";

interface SyncRunDetailDialogProps {
  run: SyncRun | null;
  onClose: () => void;
}

type RecordStatusFilter = "ALL" | "Blocked" | "Unblocked";

const RECORD_STATUS_LABELS: Record<RecordStatusFilter, string> = {
  ALL: "All",
  Blocked: "Blocked",
  Unblocked: "Unblocked",
};

const makeRecordColumns = (isLoading: boolean): ColumnDef<SyncRunRecord>[] => [
  {
    accessorKey: "employee_name",
    enableSorting: false,
    header: () => <span className="text-xs font-semibold text-greyscale-text-caption">Employee</span>,
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-32" />
      ) : (
        <span className="text-sm font-semibold text-greyscale-text-title">
          {row.original.employee_name}
        </span>
      ),
  },
  {
    accessorKey: "employee_no",
    enableSorting: false,
    header: () => <span className="text-xs font-semibold text-greyscale-text-caption">Employee No</span>,
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-20" />
      ) : (
        <span className="text-sm font-mono text-greyscale-text-caption">
          {row.original.employee_no}
        </span>
      ),
  },
  {
    accessorKey: "status",
    enableSorting: false,
    header: () => <span className="text-xs font-semibold text-greyscale-text-caption">Status</span>,
    cell: ({ row }) => {
      if (isLoading) return <Skeleton className="h-5 w-20" />;
      const meta = BLOCK_STATUS_TAG[row.original.status];
      if (!meta)
        return (
          <span className="text-sm text-greyscale-text-caption">{row.original.status}</span>
        );
      return (
        <Tag variant={meta.variant} size="sm">
          {meta.label}
        </Tag>
      );
    },
  },
  {
    accessorKey: "detail",
    enableSorting: false,
    header: () => <span className="text-xs font-semibold text-greyscale-text-caption">Detail</span>,
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-24" />
      ) : (
        <span className="text-sm text-greyscale-text-body">{row.original.detail}</span>
      ),
  },
  {
    accessorKey: "reason",
    enableSorting: false,
    header: () => <span className="text-xs font-semibold text-greyscale-text-caption">Reason</span>,
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-32" />
      ) : (
        <span className="text-sm text-greyscale-text-body">
          {row.original.reason ?? "—"}
        </span>
      ),
  },
];

const SKELETON_ROWS = Array.from({ length: 5 }, (_, i) => ({
  id: String(i),
  employee_name: "",
  employee_no: "",
  status: "Blocked" as const,
  detail: "Blocked" as const,
  reason: null,
  date_created: "",
}));

const SyncRunDetailDialog: React.FC<SyncRunDetailDialogProps> = ({ run, onClose }) => {
  const [statusFilter, setStatusFilter] = useState<RecordStatusFilter>("ALL");
  const [page, setPage] = useState(1);

  const open = run !== null;

  useEffect(() => {
    setStatusFilter("ALL");
    setPage(1);
  }, [run?.id]);

  const { data: recordsData, isLoading: recordsLoading } =
    useGetSyncRunRecordsApiService(
      {
        workflow_sync_id: run?.id ?? "",
        status: statusFilter === "ALL" ? undefined : (statusFilter as "Blocked" | "Unblocked"),
        page,
        page_size: 50,
      },
      open
    );

  // Use recordsLoading (first load only) for skeleton rows.
  // During filter/page changes, keepPreviousData keeps the previous rows visible.
  const records = recordsData?.results ?? [];
  const columns = makeRecordColumns(recordsLoading);
  const tableData = recordsLoading ? SKELETON_ROWS : records;

  const recordCount = recordsData?.count ?? 0;
  const tableHeading = `Block status changes in this sync (${recordCount})`;

  const totalSucceeded = run ? run.creations + run.updates : 0;
  const totalFailed = run ? run.failed_creations + run.failed_updates : 0;
  const totalAttempted = totalSucceeded + totalFailed;
  const passRate =
    run && totalAttempted > 0 ? Math.round((totalSucceeded / totalAttempted) * 100) : 0;

  const channelLabel = (run?.channel && CHANNEL_LABELS[run.channel]) || "-";
  const syncMeta = run ? SYNC_STATUS_TAG[run.status] : null;

  const formattedTimestamp = run
    ? new Date(run.start).toLocaleString("en-ZA", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "";

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-[95vw] min-h-[85vh] max-h-[90vh] flex flex-col">
        {run && (
          <div className="flex flex-col gap-md flex-1 min-h-0">
            <DialogHeader className="text-center">
              <div className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption">
                {formattedTimestamp}
              </div>
              <div className="flex flex-wrap items-center justify-center gap-xs">
                <DialogTitle>Sync run</DialogTitle>
                <span className="text-sm text-greyscale-text-body">{channelLabel}</span>
                {syncMeta && (
                  <Tag variant={syncMeta.variant} size="sm">
                    {syncMeta.label}
                  </Tag>
                )}
              </div>
            </DialogHeader>

            <div className="flex flex-col gap-md flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
              {/* Breakdown stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-md rounded-md bg-neutral-50 p-md">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-greyscale-text-caption">
                    Pass rate
                  </div>
                  <div className="font-heading text-base font-semibold text-greyscale-text-title">
                    {passRate}%
                  </div>
                  <div className="text-xs text-greyscale-text-caption">
                    {totalSucceeded} of {totalAttempted}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-greyscale-text-caption">
                    Created
                  </div>
                  <div className="flex items-baseline gap-xs">
                    <span className="font-heading text-base font-semibold text-greyscale-text-title">
                      {run.creations}
                    </span>
                    {run.failed_creations > 0 && (
                      <span className="text-xs font-semibold text-secondary-pink-900">
                        {run.failed_creations} failed
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-greyscale-text-caption">
                    Updated
                  </div>
                  <div className="flex items-baseline gap-xs">
                    <span className="font-heading text-base font-semibold text-greyscale-text-title">
                      {run.updates}
                    </span>
                    {run.failed_updates > 0 && (
                      <span className="text-xs font-semibold text-secondary-pink-900">
                        {run.failed_updates} failed
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-greyscale-text-caption">
                    Blocked
                  </div>
                  <div className="font-heading text-base font-semibold text-greyscale-text-title">
                    +{run.blocked}
                  </div>
                  <div className="text-xs text-greyscale-text-caption">new this run</div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-greyscale-text-caption">
                    Unblocked
                  </div>
                  <div className="font-heading text-base font-semibold text-greyscale-text-title">
                    {run.unblocked}
                  </div>
                  <div className="text-xs text-greyscale-text-caption">by this run</div>
                </div>
              </div>

              {/* Records table */}
              <div className="[&_input]:h-[46px]">
                <h4 className="text-sm font-semibold text-greyscale-text-title mb-sm">
                  {tableHeading}
                </h4>
                <DataTable
                  columns={columns}
                  data={tableData}
                  showPagination={false}
                  filterColumn="employee_name"
                  filterPlaceholder="Search employee"
                  toolbarChildren={
                    <Select
                      value={statusFilter}
                      onValueChange={(v) => { setStatusFilter(v as RecordStatusFilter); setPage(1); }}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(RECORD_STATUS_LABELS) as RecordStatusFilter[]).map((value) => (
                          <SelectItem key={value} value={value}>
                            {RECORD_STATUS_LABELS[value]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  }
                />
                {!recordsLoading && (recordsData?.pages ?? 1) > 1 && (
                  <div className="flex items-center justify-end gap-xs mt-sm">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      aria-label="Previous page"
                    >
                      <ChevronLeft size={14} />
                    </Button>
                    <span className="text-sm text-greyscale-text-caption px-xs">
                      Page {page} of {recordsData?.pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(recordsData?.pages ?? 1, p + 1))}
                      disabled={page === (recordsData?.pages ?? 1)}
                      aria-label="Next page"
                    >
                      <ChevronRight size={14} />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end pt-sm border-t border-greyscale-border">
              <Button variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SyncRunDetailDialog;
